import { useState } from 'react';
import { ActionIcon, Indicator, Loader, Popover, ScrollArea, Tabs, Text, UnstyledButton } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { isLimitedAdminScope } from '@/helpers';
import {
  useApproveNotificationMutation,
  useDismissAllNotificationsMutation,
  useDismissNotificationMutation,
  useGlobalSettings,
  useNotifications,
  useRejectNotificationMutation,
} from '@/hooks';
import { NOTIFICATION_FILTER } from '@/types';
import { NotificationItem } from './NotificationItem';
import styles from './NotificationsPopover.module.sass';

const NOTIFICATIONS_PAGE_LIMIT = 50;
const UNREAD_INDICATOR_MAX = 99;

const NOTIFICATION_FILTER_TABS = [
  { value: NOTIFICATION_FILTER.UNREAD, label: 'Unread' },
  { value: NOTIFICATION_FILTER.ALL, label: 'All' },
];

export const NotificationsPopover = () => {
  const { currentChurch } = useGlobalSettings();
  const churchId = currentChurch?.churchId ?? '';
  const isLimitedScope = isLimitedAdminScope({
    isLimitedScope: currentChurch?.isLimitedScope,
    isGroupScope: currentChurch?.isGroupScope,
  });
  const isNotificationsEnabled = Boolean(churchId) && !isLimitedScope;
  const [opened, setOpened] = useState(false);
  const [activeFilter, setActiveFilter] = useState<NOTIFICATION_FILTER>(NOTIFICATION_FILTER.UNREAD);
  const isUnreadTab = activeFilter === NOTIFICATION_FILTER.UNREAD;

  // Unread is a server filter. Approved or rejected items can still have read: false and belong on All only.
  const unreadNotificationsQuery = useNotifications({
    churchId,
    limit: NOTIFICATIONS_PAGE_LIMIT,
    filter: NOTIFICATION_FILTER.UNREAD,
    enabled: isNotificationsEnabled,
  });
  const allNotificationsQuery = useNotifications({
    churchId,
    limit: NOTIFICATIONS_PAGE_LIMIT,
    filter: NOTIFICATION_FILTER.ALL,
    enabled: isNotificationsEnabled && opened && !isUnreadTab,
  });
  const approveNotificationMutation = useApproveNotificationMutation();
  const rejectNotificationMutation = useRejectNotificationMutation();
  const dismissNotificationMutation = useDismissNotificationMutation();
  const dismissAllNotificationsMutation = useDismissAllNotificationsMutation();

  const activeNotificationsQuery = isUnreadTab ? unreadNotificationsQuery : allNotificationsQuery;
  const notificationItems = activeNotificationsQuery.data?.items ?? [];
  const unreadTotal = unreadNotificationsQuery.data?.total ?? 0;
  const unreadIndicatorLabel = unreadTotal > UNREAD_INDICATOR_MAX ? `${UNREAD_INDICATOR_MAX}+` : unreadTotal;

  const handleToggleOpened = () => {
    setActiveFilter(NOTIFICATION_FILTER.UNREAD);
    setOpened((isCurrentlyOpened) => !isCurrentlyOpened);
  };

  const handleOpenChange = (nextOpened: boolean) => {
    if (nextOpened) {
      setActiveFilter(NOTIFICATION_FILTER.UNREAD);
    }
    setOpened(nextOpened);
  };

  const handleFilterChange = (value: string | null) => {
    if (value === NOTIFICATION_FILTER.UNREAD || value === NOTIFICATION_FILTER.ALL) {
      setActiveFilter(value);
    }
  };

  const handleApproveNotificationClick = ({ notificationId }: { notificationId: string }) => {
    approveNotificationMutation.mutate({
      churchId,
      id: notificationId,
    });
  };

  const handleRejectNotificationClick = ({ notificationId }: { notificationId: string }) => {
    rejectNotificationMutation.mutate({
      churchId,
      id: notificationId,
    });
  };

  const handleDismissNotificationClick = ({ notificationId }: { notificationId: string }) => {
    dismissNotificationMutation.mutate({
      churchId,
      id: notificationId,
    });
  };

  const handleDismissAllNotificationsClick = () => {
    dismissAllNotificationsMutation.mutate({
      churchId,
    });
  };

  if (!churchId || isLimitedScope) {
    return null;
  }

  const showLoader = activeNotificationsQuery.isLoading || activeNotificationsQuery.isFetching;
  const isDismissAllDisabled = dismissAllNotificationsMutation.isPending || unreadTotal === 0;

  return (
    <Popover
      opened={opened}
      onChange={handleOpenChange}
      position="bottom-end"
      width={400}
      withArrow={false}
      shadow="md"
      radius="md"
      zIndex={150}
      classNames={{ dropdown: styles.dropdown }}
    >
      <Popover.Target>
        <Indicator
          disabled={unreadTotal === 0}
          label={unreadIndicatorLabel}
          size={18}
          color="brand.7"
          offset={4}
          position="top-end"
          zIndex={1}
          classNames={{ indicator: styles.unreadIndicator }}
        >
          <ActionIcon
            variant="light"
            size="md"
            radius="xl"
            color="base"
            onClick={handleToggleOpened}
            aria-label={unreadTotal > 0 ? `Open notifications, ${unreadTotal} unread` : 'Open notifications'}
          >
            <IconBell size={20} stroke={1.5} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown>
        <div className={styles.inner}>
          <div className={styles.header}>
            <Text component="h2" className={styles.title}>
              Notifications
            </Text>
            {isUnreadTab ? (
              <UnstyledButton
                type="button"
                className={styles.markRead}
                onClick={handleDismissAllNotificationsClick}
                aria-label="Mark all notifications as read"
                disabled={isDismissAllDisabled}
              >
                Mark all as read
              </UnstyledButton>
            ) : null}
          </div>

          <Tabs
            value={activeFilter}
            onChange={handleFilterChange}
            classNames={{ list: styles.tabsList, tab: styles.tab }}
          >
            <Tabs.List>
              {NOTIFICATION_FILTER_TABS.map((tab) => (
                <Tabs.Tab key={tab.value} value={tab.value}>
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>

          {showLoader ? (
            <div className={styles.loaderWrap}>
              <Loader size="sm" />
            </div>
          ) : notificationItems.length === 0 ? (
            <Text component="p" className={styles.empty}>
              No notifications
            </Text>
          ) : (
            <ScrollArea className={styles.list} type="auto" offsetScrollbars>
              {notificationItems.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onApprove={handleApproveNotificationClick}
                  onReject={handleRejectNotificationClick}
                  onDismiss={handleDismissNotificationClick}
                  isApproveLoading={
                    approveNotificationMutation.isPending &&
                    approveNotificationMutation.variables?.id === notification.id
                  }
                  isRejectLoading={
                    rejectNotificationMutation.isPending && rejectNotificationMutation.variables?.id === notification.id
                  }
                  isDismissLoading={
                    dismissNotificationMutation.isPending &&
                    dismissNotificationMutation.variables?.id === notification.id
                  }
                />
              ))}
            </ScrollArea>
          )}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};
